import { useTranslation } from 'react-i18next';
import { Area } from './types';
import { getAreaColor } from './color-utils';

interface AreasListProps {
    areas: Area[];
    selectedAreaId: number | null;
    onSelectArea: (area: Area) => void;
    onEdit: (area: Area) => void;
    onDelete: (id: number) => void;
}

export const AreasList = ({ areas, selectedAreaId, onSelectArea, onEdit, onDelete }: AreasListProps) => {
    const { t } = useTranslation() as { t: (key: string) => string };

    const visibleAreas = selectedAreaId != null ? areas.filter(a => a.id === selectedAreaId) : areas;
    
    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full flex flex-col overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                <h3 className="text-lg font-semibold text-gray-800">{t('common.label.lista-areas')}</h3>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {visibleAreas.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                        <svg
                            className="mx-auto h-12 w-12 text-gray-400 mb-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                            />
                        </svg>
                        <p className="text-sm">{t('common.label.no-areas')}</p>
                    </div>
                ) : (
                    visibleAreas.map((area) => (
                        <div
                            key={area.id}
                            className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-all bg-white cursor-pointer"
                            style={{ borderLeftWidth: '4px', borderLeftColor: getAreaColor(area.id) }}
                            onClick={() => {
                                onSelectArea(area);
                                onEdit(area);
                            }}
                        >
                            <div className="flex items-start justify-between mb-2">
                                <h4 className="font-semibold text-gray-800 flex-1 break-words">{area.name}</h4>
                                <div
                                    className="w-3 h-3 rounded-full flex-shrink-0 ml-2"
                                    style={{ backgroundColor: getAreaColor(area.id) }}
                                />
                            </div>

                            {area.description && (
                                <p className="text-xs text-gray-600 mb-2 line-clamp-2">{area.description}</p>
                            )}

                            <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                                <span className="flex items-center gap-1">
                                    <svg
                                        className="w-3 h-3"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                        />
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                        />
                                    </svg>
                                    {area.center_lat.toFixed(4)}, {area.center_lon.toFixed(4)}
                                </span>
                            </div>

                            <div className="flex items-center justify-between">
                                <span
                                    className="text-xs font-semibold px-2 py-1 rounded"
                                    style={{ backgroundColor: `${getAreaColor(area.id)}20`, color: getAreaColor(area.id) }}
                                >
                                    ⭕ {area.radius} km
                                </span>

                                {/* <div className="flex gap-2">
                                    <button
                                        onClick={() => onEdit(area)}
                                        className="p-1.5 text-[#006bc6] hover:bg-blue-50 rounded transition-all"
                                        title={t('common.label.edit')}
                                    >
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                            />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => area.id && onDelete(area.id)}
                                        className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-all"
                                        title={t('common.label.delete')}
                                    >
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                            />
                                        </svg>
                                    </button>
                                </div> */}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
